import { makeAutoObservable } from "mobx";
import { AttributeShelf, LoaderShelf } from "../../shelves";
import type { AuthenticationStore } from "../Authentication";
import { api } from "../../services";
import { ValidationTypeOTP } from "../../enum/validationTypeOTP";
import { getPublicIp } from "../../services/GetPublicIP";
import type { Authenticity, SendOTPValidationReq, ValidateCodeOTPReq } from "../../services/APIService/types";
import type { FintechRegisterStore } from "../FintechRegisterStore";
import { LOCAL_STORAGE_SESSION_ID } from "../../constants";
import { TemplateSMSEmail } from "../../enum/templateSMSEmail";

interface ValidationOTPRes {
	isValid: boolean;
}

export class ValidationOTPStore {
	public authentication: AuthenticationStore;
	public fintechRegisterStore: FintechRegisterStore;
	public loader = new LoaderShelf();
	public requestIdOTP = new AttributeShelf<string>("");
	public validationId = new AttributeShelf<string>("");
	public remainingValidationAttempts = new AttributeShelf<number>(3);

	constructor(authentication: AuthenticationStore, fintechRegisterStore: FintechRegisterStore) {
		this.authentication = authentication;
		this.fintechRegisterStore = fintechRegisterStore;
		makeAutoObservable(this);
	}

	// Método de acesso ao requestIdOTP
	public get getRequestId(): string {
		return this.requestIdOTP.value;
	}

	// Método de acesso ao validatedIdOTP
	public get getValidationId(): string {
		return this.validationId.value;
	}

	// Método de acesso ao remainingValidationAttempts
	public get remainingAttempts(): number {
		return this.remainingValidationAttempts.value;
	}

	// Método de reset de remainingValidationAttempts
	public resetRemainingAttempts(): void {
		this.remainingValidationAttempts.set(3);
	}

	// Método de envio de OTP
	public sendValidationOTP = async (type: ValidationTypeOTP): Promise<void> => {
		if (this.loader.isLoading) {
			return;
		}
		this.loader.start();

		const authenticity = await this.getAuthenticity();
		const template = this.getTemplate(type);

		const body: SendOTPValidationReq = {
			validationType: ValidationTypeOTP[type],
			authenticity,
			contact: this.contact(type),
			// Só inclui templateCode se template existir
			...(template ? { templateCode: template } : {}),
		};

		try {
			const data = await api.sendValidationOTPToken(this.authentication.getToken() || "", body);
			this.requestIdOTP.set(data.id);
		} catch (error) {
		} finally {
			this.loader.end();
		}
	};

	// Método de validação de OTP
	public validateOTP = async (code: string): Promise<ValidationOTPRes> => {
		const authenticity = await this.getAuthenticity();

		const body: ValidateCodeOTPReq = {
			id: this.requestIdOTP.value,
			token: code,
			authenticity,
		};

		try {
			this.loader.start();
			const res = await api.postValidateOTP(this.authentication.getToken() || "", body);
			this.validationId.set(res.validationId);

			return { isValid: true };
		} catch (error) {
			this.remainingValidationAttempts.set(this.remainingValidationAttempts.value - 1);
			return { isValid: false };
		} finally {
			this.loader.end();
		}
	};

	// Método de acesso ao objeto de autenticidade
	public getAuthenticity = async (): Promise<Authenticity> => {
		const ip = await getPublicIp();
		const hashID = localStorage.getItem(LOCAL_STORAGE_SESSION_ID);

		return {
			fingerprint: hashID || "",
			ipAddress: ip,
			sessionId: hashID || "",
			timestamp: new Date().toISOString(),
		};
	};

	// Método de acesso ao contato do usuário
	private contact(type: ValidationTypeOTP): string | null {
		const { mainEmail } = this.fintechRegisterStore;
		const mail = this.fintechRegisterStore.getNewEmail;
		const { currentUser } = this.authentication;

		switch (type) {
			case ValidationTypeOTP.EMAIL:
				return mail || mainEmail.value;
			case ValidationTypeOTP.PHONE:
				return currentUser!.phoneNumber;
			default:
				return null;
		}
	}

	private getTemplate(type: ValidationTypeOTP): TemplateSMSEmail | null {
		switch (type) {
			case ValidationTypeOTP.EMAIL:
				return TemplateSMSEmail.EMAIL_FGTS_VALID;
			case ValidationTypeOTP.PASSWORD_RESET:
				return TemplateSMSEmail.SMS_ANTFGTS_STRANS;
			default:
				return null;
		}
	}
}
